import styled, { css } from 'styled-components';
import { map } from '@lendi-ui/breakpoint';
import { Spacing } from '@lendi-ui/spacing';

var gutter = {
  xs: '0.875rem',
  sm: '1rem',
  md: '1.25rem',
  lg: '1.5rem'
};

function halignMixin(_ref) {
  var {
    halign,
    reverse
  } = _ref;

  // if no value is specified, then don't output any css (it just makes it harder for the consumer to override)
  if (halign === undefined) {
    return '';
  }

  return map(halign, function () {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'left';
    var rule = '';

    switch (value) {
      case 'left':
        if (reverse) {
          rule = 'flex-end';
        } else {
          rule = 'flex-start';
        }

        break;

      case 'right':
        if (reverse) {
          rule = 'flex-start';
        } else {
          rule = 'flex-end';
        }

        break;

      case 'center':
        rule = 'center';
        break;

      case 'justify-center':
        rule = 'space-around';
        break;

      case 'justify':
        rule = 'space-between';
        break;

      default:
        throw new Error("styled-components-grid: halign must be one of \"left\", \"right\", \"center\", \"justify-center\" or \"justify\". Got \"".concat(String(value), "\""));
    }

    return "justify-content: ".concat(rule, ";");
  });
}

function valignMixin(_ref2) {
  var {
    valign
  } = _ref2;

  // if no value is specified, then don't output any css (it just makes it harder for the consumer to override)
  if (valign === undefined) {
    return '';
  }

  return map(valign, function () {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'stretch';
    var rule = '';

    switch (value) {
      case 'top':
        rule = 'flex-start';
        break;

      case 'bottom':
        rule = 'flex-end';
        break;

      case 'center':
        rule = 'center';
        break;

      case 'stretch':
        rule = 'stretch';
        break;

      default:
        throw new Error("styled-components-grid: valign must be one of \"top\", \"bottom\", \"center\" or \"stretch\". Got \"".concat(String(value), "\"."));
    }

    return "align-items: ".concat(rule, ";");
  });
}

function reverseMixin(_ref3) {
  var {
    reverse
  } = _ref3;

  // if no value is specified, then don't output any css (it just makes it harder for the consumer to override)
  if (reverse === undefined) {
    return '';
  }

  return map(reverse, function () {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    return "flex-direction: ".concat(value && 'row-reverse' || 'row', ";");
  });
}

function wrapMixin(_ref4) {
  var {
    wrap,
    reverse
  } = _ref4;

  if (wrap === undefined) {
    return 'flex-wrap: wrap;';
  }

  return map(wrap, function () {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var breakpoint = arguments.length > 1 ? arguments[1] : undefined;
    var reverseVal = typeof reverse === 'object' && breakpoint ? reverse[breakpoint] : reverse;

    if (value && reverseVal) {
      return 'flex-wrap: wrap-reverse;';
    } else if (value === false) {
      return 'flex-wrap: nowrap;';
    } else {
      return 'flex-wrap: wrap;';
    }
  });
}

function isSpacingName(gutter) {
  return Spacing[gutter] !== undefined;
}

function gutterMixin(_ref5) {
  var {
    gutter: gutter$1 = gutter
  } = _ref5;
  return map(gutter$1, value => {
    if (value === undefined) return;
    var val = isSpacingName(value) ? Spacing[value] : value;
    return "\n        width: calc(100% + ".concat(val, ");\n        margin-left: calc(").concat(val, " / 2 * -1);\n        margin-right: calc(").concat(val, " / 2 * -1);\n\n        & > * {\n          padding-left: calc(").concat(val, " / 2);\n          padding-right: calc(").concat(val, " / 2);\n        }\n      ");
  });
}

function grid() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return css(["display:flex;", ";", ";", ";", ";", ";"], halignMixin(props), valignMixin(props), reverseMixin(props), wrapMixin(props), gutterMixin(props));
}

function offsetMixin(_ref) {
  var {
    offset
  } = _ref;

  if (offset === undefined) {
    return undefined;
  }

  return map(offset, value => {
    if (value !== undefined) {
      var pct = Math.round(value * 100 * 10000) / 10000; // round to 4 decimal places

      return "margin-left: ".concat(pct, "%;");
    }

    return undefined;
  });
}

function sizeMixin(_ref2) {
  var {
    size = 1
  } = _ref2;
  return map(size, function () {
    var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;

    switch (value) {
      case 'min':
        return "\n          flex-grow: 0;\n          flex-basis: auto;\n          width: auto;\n          max-width: none;\n        ";

      case 'max':
        return "\n          flex-grow: 1;\n          flex-basis: auto;\n          width: auto;\n          max-width: none;\n          max-width: 100%; /* TODO: does this always work as expected? */\n        ";

      default:
        {
          var pct = Math.round(value * 100 * 10000) / 10000; // round to 4 decimal places

          return "\n          flex-basis: ".concat(pct, "%;\n          max-width: ").concat(pct, "%;\n        ");
        }
    }
  });
}

function unit() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return css(["box-sizing:border-box;", ";", ";"], offsetMixin(props), sizeMixin(props));
}

var Unit = styled.div.withConfig({
  displayName: "src__Unit",
  componentId: "lui__sc-1606zaz-0"
})(["", ";"], unit);
var Grid = styled.div.withConfig({
  displayName: "src__Grid",
  componentId: "lui__sc-1606zaz-1"
})(["", ";"], grid);

/*
Styled components asserts that its components have no other properties on it,
however we have broken that promise in how we created this component.

We are ignoring the illegal assignment below, and casting the type of the export
so we do not make a breaking change for consumers.
*/
// @ts-expect-error
Grid.Unit = Unit;

export { Grid, Unit, Grid as default, grid, unit };
